/**
 * @file Public landing page — pages/Landing/ResearchPage.tsx.
 */

import React from "react";
import { Link, useNavigate } from "react-router-dom";
import "./LandingPage.css";
import "./ContentPage.css";

const ResearchPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="landing-root">
      {/* Top Header */}
      <header className="landing-header flex justify-center items-center space-x-3 text-center py-4">
        <h1 className="landing-main-title text-4xl font-bold text-primary">
          ICoMEC
        </h1>
        <h2 className="landing-sub-title text-xl text-grayDark">
          Indian Construction Materials Embodied Carbon Data Base
        </h2>
      </header>

      {/* Navigation Bar */}
      <nav className="landing-nav">
        <Link to="/">Home</Link>
        <Link to="/join">Join</Link>
        <Link to="/impact">Impact</Link>
        <Link to="/research">Research and Analysis</Link>
        <Link to="/about">About</Link>
      </nav>

      {/* Content Section */}
      <section className="content-page">
        <h1 className="content-title">Research and Analysis</h1>
        
        <div className="content-section">
          <h2>Built on Rigor, Defined by Transparency</h2>
          <p>
            At ICoMEC, we believe a number is only as good as the data behind it.
          </p>
          <p>
            Our tool is built on the core principles of Life Cycle Assessment (LCA), but we take it a step further. We don't just present carbon data; we quantify the confidence of your results.
          </p>
        </div>

        <div className="content-section">
          <h2>Why Our Research Approach Is Different</h2>
          <p>
            In the complex world of global supply chains, data is rarely perfect. We believe uncertainty should be communicated, not hidden.
          </p>
          <ul className="content-list">
            <li><strong>Context-Driven:</strong> We account for regional differences in power grids and manufacturing methods.</li>
            <li><strong>Statistical Integrity:</strong> Our framework uses statistical modeling to show you the range of possible outcomes.</li>
            <li><strong>Radial Transparency:</strong> We provide a clear trail back to the source data for every calculation.</li>
          </ul>
        </div>

        <div className="content-section">
          <h2>A Living Database</h2>
          <p>
            Science doesn't stand still, and neither do we. The ICoMEC framework is a dynamic system that evolves as new data becomes available.
          </p>
          <ul className="content-list">
            <li><strong>Statistical Framework:</strong> Want to see the math? Our publication offers a deep dive into how we handle variance and data correlation.</li>
            <li><strong>Always Current:</strong> New datasets are integrated at regular intervals to ensure your reports reflect the current state of the industry.</li>
          </ul>
        </div>

        <div className="content-cta-box">
          <p>Ready to explore the data?</p>
          <p>Gain full access to our underlying datasets and see the ICoMEC framework in action.</p>
          <div className="content-buttons">
            <button 
              className="content-btn content-btn-primary"
              onClick={() => navigate("/login?role=PUBLIC")}
            >
              Access the Database
            </button>
            <button 
              className="content-btn content-btn-secondary"
              onClick={() => navigate("/research-paper")}
            >
              Read the Research Paper
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="footer-main">
          <div className="footer-left">
            <h2>ICoMEC</h2>
            <p>
              Indian Construction Materials Embodied
              <br /> Carbon Data Base
            </p>
          </div>

          <div className="footer-links">
            <div>
              <h4>About Us</h4>
              <ul>
                <li><Link to="/who-we-are">Who We Are</Link></li>
                <li><Link to="/what-we-do">What We Do</Link></li>
                <li><Link to="/leadership">Leadership</Link></li>
                <li><Link to="/partners">Partners</Link></li>
                <li><Link to="/faq">FAQ</Link></li>
                <li><Link to="/glossary">Glossary</Link></li>
              </ul>
            </div>
            <div>
              <h4>Resources</h4>
              <ul>
                {/* <li><Link to="/resource-library">Resource Library</Link></li> */}
                <li><Link to="/events">Events</Link></li>
              </ul>
            </div>
          </div>
        </div>
      </footer>

      {/* Footer Bottom */}
      <div className="footer-bottom">
        <p>
          <Link to="/cookies-policy">Cookies Policy</Link> · <Link to="/privacy-policy">Privacy Policy</Link> · <Link to="/legal">Legal</Link> · <Link to="/terms-of-use">Terms of Use</Link>
          {/* · <Link to="/imprint">Imprint</Link> */}
        </p>
        <p>© 2026 CARBSE. All rights reserved.</p>
      </div>
    </div>
  );
};

/** React component — Research Page. */
export default ResearchPage;
